import React, { useMemo, useState } from "react";
import { Modal, Select, Button, Space, Typography, Tag, Empty } from "antd";
import { PrinterOutlined, TagsOutlined } from "@ant-design/icons";
import JsBarcode from "jsbarcode";

const { Text } = Typography;

const LAYOUTS = [
  { key: "30x20", label: "30mm x 20mm (Small / Jewelry)", width: 30, height: 20, nameFontSize: 6, priceFontSize: 5, unitFontSize: 4, barcodeHeightRatio: 0.6 },
  { key: "40x30", label: "40mm x 30mm", width: 40, height: 30, nameFontSize: 7, priceFontSize: 6, unitFontSize: 5, barcodeHeightRatio: 0.62 },
  { key: "50x25", label: "50mm x 25mm (Standard Thermal)", width: 50, height: 25, nameFontSize: 8, priceFontSize: 7, unitFontSize: 6, barcodeHeightRatio: 0.64 },
  { key: "58x40", label: "58mm x 40mm (Large Thermal)", width: 58, height: 40, nameFontSize: 10, priceFontSize: 9, unitFontSize: 7, barcodeHeightRatio: 0.6 },
  { key: "100x50", label: "100mm x 50mm (Shipping Label)", width: 100, height: 50, nameFontSize: 12, priceFontSize: 11, unitFontSize: 9, barcodeHeightRatio: 0.6 },
];

const COLUMN_OPTIONS = [
  { value: 1, label: "1 Column" },
  { value: 2, label: "2 Columns" },
  { value: 3, label: "3 Columns" },
  { value: 4, label: "4 Columns" },
];

const GAP_MM = 2; // gap between labels on the same row/column
const PREVIEW_SCALE = 3.2; // px per mm, for on-screen preview only

const chunk = (arr, size) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

const BarcodeModal = ({ isOpen, onClose, items }) => {
  const [layoutKey, setLayoutKey] = useState("50x25");
  const [columns, setColumns] = useState(1);
  const [barcodeCache, setBarcodeCache] = useState({});

  const layout = useMemo(() => LAYOUTS.find((l) => l.key === layoutKey) || LAYOUTS[2], [layoutKey]);

  const generateBarcode = (itemId) => {
    if (barcodeCache[itemId]) {
      return barcodeCache[itemId];
    }

    if (!itemId) {
      return null;
    }

    const canvas = document.createElement("canvas");
    try {
      canvas.width = 400;
      canvas.height = 100;

      JsBarcode(canvas, itemId.toString(), {
        format: "CODE128",
        width: 3,
        height: 60,
        displayValue: true,
        fontSize: 12,
        textMargin: 2,
        background: "#ffffff",
        lineColor: "#000000",
        margin: 2,
      });

      const dataURL = canvas.toDataURL();
      setBarcodeCache((prev) => ({ ...prev, [itemId]: dataURL }));
      return dataURL;
    } catch (error) {
      console.error("Error generating barcode for item ID:", itemId, error);
      return null;
    }
  };

  const handlePrint = () => {
    const { width, height, nameFontSize, priceFontSize, unitFontSize, barcodeHeightRatio } = layout;
    const barcodeHeight = (height * barcodeHeightRatio).toFixed(2);
    const nameHeight = (height * 0.24).toFixed(2);
    const detailsHeight = (height * 0.1).toFixed(2);
    const pageWidth = (width * columns + GAP_MM * (columns - 1)).toFixed(2);

    const rows = chunk(items || [], columns);

    const rowsHtml = rows.map((rowItems) => {
      const labelsHtml = rowItems.map((item) => {
        const barcodeSrc = generateBarcode(item.id);
        const price = item.offerprice || item.mrp || '0';
        return `
          <div class="thermal-label">
            <div class="item-name">${item.iname || ''}</div>
            <div class="barcode-container">
              ${barcodeSrc
                ? `<img class="barcode-image" src="${barcodeSrc}" alt="Barcode for ${item.iname}" />`
                : `<div style="font-size:8px;color:#666;">Barcode Error: ID ${item.id}</div>`}
            </div>
            <div class="item-details">
              <div class="item-price">฿${parseFloat(price).toFixed(2)}</div>
              <div class="item-unit">${item.unit || ''}</div>
            </div>
          </div>
        `;
      }).join('');
      return `<div class="label-row">${labelsHtml}</div>`;
    }).join('');

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Barcode Labels - ${pageWidth}mm x ${height}mm</title>
          <style>
            @page {
              size: ${pageWidth}mm ${height}mm;
              margin: 0;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              background: white;
            }
            .label-row {
              display: flex;
              width: ${pageWidth}mm;
              gap: ${GAP_MM}mm;
              page-break-after: always;
              box-sizing: border-box;
            }
            .label-row:last-child {
              page-break-after: avoid;
            }
            .thermal-label {
              width: ${width}mm;
              height: ${height}mm;
              padding: 0.5mm;
              margin: 0;
              border: 1px solid #000;
              display: block;
              text-align: center;
              background: white;
              box-sizing: border-box;
              position: relative;
              flex-shrink: 0;
            }
            .item-name {
              font-size: ${nameFontSize}px;
              font-weight: bold;
              margin: 0 0 0.5mm 0;
              height: ${nameHeight}mm;
              overflow: hidden;
              line-height: 1.0;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .barcode-container {
              height: ${barcodeHeight}mm;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0.5mm 0;
            }
            .barcode-image {
              max-width: ${width - 1}mm;
              max-height: ${barcodeHeight}mm;
              object-fit: contain;
            }
            .item-details {
              font-size: ${unitFontSize}px;
              margin: 0;
              height: ${detailsHeight}mm;
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
              line-height: 1.0;
              padding: 0 1mm;
            }
            .item-price {
              font-size: ${priceFontSize}px;
              font-weight: bold;
              color: #000;
              margin: 0;
            }
            .item-unit {
              font-size: ${unitFontSize}px;
              margin: 0;
            }
            @media print {
              body {
                margin: 0;
                padding: 0;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .label-row {
                page-break-inside: avoid;
              }
              .thermal-label {
                page-break-inside: avoid;
              }
              @page {
                size: ${pageWidth}mm ${height}mm;
                margin: 0;
              }
            }
          </style>
        </head>
        <body>
          ${rowsHtml}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const previewWidth = layout.width * PREVIEW_SCALE;
  const previewHeight = layout.height * PREVIEW_SCALE;

  return (
    <Modal
      title={
        <Space>
          <TagsOutlined style={{ color: '#0d9488' }} />
          <span>Barcode Label Printer</span>
        </Space>
      }
      open={isOpen}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>Close</Button>,
        <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrint} disabled={!items || items.length === 0}>
          Print Labels
        </Button>,
      ]}
      destroyOnClose
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f0fdfa',
          border: '1px solid #99f6e4',
          borderRadius: 10,
          padding: 12,
          marginBottom: 16,
        }}
      >
        <Space wrap>
          <Text style={{ fontSize: 13 }}>Print Layout</Text>
          <Select
            value={layoutKey}
            onChange={setLayoutKey}
            style={{ width: 260 }}
            options={LAYOUTS.map((l) => ({ value: l.key, label: l.label }))}
          />
          <Text style={{ fontSize: 13 }}>Columns</Text>
          <Select
            value={columns}
            onChange={setColumns}
            style={{ width: 120 }}
            options={COLUMN_OPTIONS}
          />
          <Tag color="teal" style={{ color: '#0d9488', borderColor: '#5eead4', background: '#f0fdfa' }}>
            Paper width: {(layout.width * columns + GAP_MM * (columns - 1)).toFixed(0)}mm
          </Tag>
        </Space>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Items loaded: {items ? items.length : 0}
        </Text>
      </div>

      <div
        style={{
          maxHeight: '55vh',
          overflowY: 'auto',
          padding: 16,
          background: '#fafafa',
          border: '1px solid #f0f0f0',
          borderRadius: 10,
        }}
      >
        {items && items.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, ${previewWidth}px)`,
              gap: GAP_MM * PREVIEW_SCALE,
              justifyContent: 'center',
            }}
          >
            {items.map((item) => {
              const barcodeSrc = generateBarcode(item.id);
              return (
                <div
                  key={item.id}
                  style={{
                    width: previewWidth,
                    height: previewHeight,
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 4,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                  }}
                >
                  <Text
                    style={{
                      fontSize: Math.max(layout.nameFontSize * (PREVIEW_SCALE / 3), 9),
                      fontWeight: 600,
                      textAlign: 'center',
                      lineHeight: 1.1,
                      width: '100%',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.iname}
                  </Text>
                  {barcodeSrc ? (
                    <img
                      src={barcodeSrc}
                      alt={`Barcode for ${item.iname}`}
                      style={{ maxWidth: '95%', maxHeight: previewHeight * layout.barcodeHeightRatio, objectFit: 'contain' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <Text type="secondary" style={{ fontSize: 10 }}>No barcode</Text>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: Math.max(layout.priceFontSize * (PREVIEW_SCALE / 3), 9) }}>
                    <Text strong style={{ fontSize: 'inherit' }}>{'฿'}{parseFloat(item.offerprice || item.mrp || 0).toFixed(2)}</Text>
                    <Text type="secondary" style={{ fontSize: 'inherit' }}>{item.unit || ''}</Text>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <Empty description="No items available for barcode generation" />
        )}
      </div>
    </Modal>
  );
};

export default BarcodeModal;
